/* eslint-disable @typescript-eslint/no-explicit-any */
/* Sticky live summary / preview (right rail). Reuses the pure calc functions
   (no second calculation engine), debounced 200ms so it never blocks typing.
   Content adapts to the structure type's template. */
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { StructureType, TYPE_BADGE, templateForType } from "./salaryStructure.config";
import {
  computeEmployeePreview,
  computeContractPreview,
  computeTraineePreview,
  monthsLabel,
  formatINR,
} from "./salaryStructure.calc";
import SalaryCalculationGuide from "./SalaryCalculationGuide";

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex items-center justify-between py-1 ${strong ? "font-semibold" : ""}`}>
    <span className="text-[12px] text-slate-500 dark:text-slate-400">{label}</span>
    <span className="text-[12px] text-slate-800 dark:text-slate-100">{value}</span>
  </div>
);

const NetCard = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-[12px] bg-slate-900 dark:bg-black/60 text-white px-3 py-2.5 my-2">
    <p className="text-[10px] uppercase tracking-wide text-slate-300">{label}</p>
    <p className="text-lg font-bold">{value}</p>
  </div>
);

const StatusPill = ({ on, label }: { on: boolean; label: string }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
      on
        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
        : "bg-slate-100 text-slate-400 dark:bg-gray-700 dark:text-gray-500"
    }`}
  >
    {label} {on ? "✓" : "✕"}
  </span>
);

const SummaryPanel = ({ structureType }: { structureType: StructureType }) => {
  const { watch } = useFormContext();
  const values = watch();
  const [ctc, setCtc] = useState(600000);

  const [snapshot, setSnapshot] = useState<any>(values);
  const valuesKey = JSON.stringify(values);
  useEffect(() => {
    const t = window.setTimeout(() => setSnapshot(values), 200);
    return () => window.clearTimeout(t);
  }, [valuesKey, ctc, values]);

  const badge = TYPE_BADGE[structureType];
  const template = templateForType(structureType);

  const body = () => {
    if (template === "EMPLOYEE") {
      const p = computeEmployeePreview(snapshot, ctc);
      return (
        <>
          <label className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">Example Annual CTC</label>
          <input
            type="number"
            value={ctc}
            onChange={(e) => setCtc(Number(e.target.value) || 0)}
            className="w-full mb-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <Row label="Basic Salary" value={formatINR(p.monthlyBasic)} />
          <Row label="HRA" value={formatINR(p.monthlyHra)} />
          <Row label="Conveyance" value={formatINR(p.monthlyConveyance)} />
          <Row label="Medical" value={formatINR(p.monthlyMedical)} />
          <Row label="Special Allowance" value={formatINR(p.monthlySpecial)} />
          <div className="border-t border-slate-200 dark:border-gray-600 my-1" />
          <Row label="Monthly Gross" value={formatINR(p.monthlyGross)} strong />
          <Row label="PF (Employee)" value={`− ${formatINR(p.pfEmployee)}`} />
          <Row label="Professional Tax" value={`− ${formatINR(p.professionalTax)}`} />
          <NetCard label="Net Take-home / month" value={formatINR(p.netTakeHome)} />
          <Row label="Employer Cost / month" value={formatINR(p.employerCostMonthly)} />
          <div className="flex flex-wrap gap-1.5 mt-3">
            <StatusPill on={Boolean(values.pfApplicable)} label="PF" />
            <StatusPill on={Boolean(values.esiApplicable)} label="ESI" />
            <StatusPill on={Boolean(values.profTaxApplicable)} label="PT" />
          </div>
        </>
      );
    }
    if (template === "CONTRACT") {
      const p = computeContractPreview(snapshot);
      return (
        <>
          <Row label="Contract Type" value={p.isGst ? "With GST" : "Without GST"} />
          <Row label="Annual Contract Value" value={formatINR(p.annualContractValue)} />
          <Row label="Monthly Payout" value={formatINR(p.monthlyPayout)} />
          {p.isGst && <Row label={`GST (${p.gstPercentage}%)`} value={`+ ${formatINR(p.gstAmount)}`} />}
          {p.isGst && <Row label="Invoice Total" value={formatINR(p.invoiceTotal)} strong />}
        </>
      );
    }
    const p = computeTraineePreview(snapshot);
    return (
      <>
        <Row label="Monthly Stipend" value={formatINR(p.monthlyStipend)} />
        <Row label="Training Period" value={monthsLabel(p.months)} />
        <NetCard label="Total Cost" value={formatINR(p.totalCost)} />
        <div className="flex flex-wrap gap-1.5 mt-1">
          <StatusPill on={Boolean(values.pfApplicable)} label="PF" />
          <StatusPill on={Boolean(values.esiApplicable)} label="ESI" />
          <StatusPill on={Boolean(values.insuranceApplicable)} label="Insurance" />
        </div>
      </>
    );
  };

  return (
    <aside className="w-full min-w-0 lg:sticky lg:top-4">
      <div className="rounded-[14px] border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-slate-800 dark:text-white">Live Summary</p>
          <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${badge.className}`}>{badge.label}</span>
        </div>
        {values.structureName && (
          <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-200 mb-2 truncate">
            {values.structureName}
          </p>
        )}
        {body()}
      </div>

      {/* Authoritative calculation steps & formula from the server, so the
          summary above is grounded in the same logic used for actual salaries. */}
      <SalaryCalculationGuide />
    </aside>
  );
};

export default SummaryPanel;
